/**
 * React Query hook for fetching user notifications
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserNotifications, type Notification } from '@/services/notifications/notificationService';
import { notificationPreferenceKeys } from './useNotificationPreferences';

export const notificationKeys = {
  all: ['notifications'] as const,
  // limit/offset are part of the identity: the 10-item dropdown and the 50-item
  // page must not share (and overwrite) the same cache entry.
  list: (options?: { unreadOnly?: boolean; limit?: number; offset?: number }) =>
    [...notificationKeys.all, 'list', options] as const,
};

export interface UseNotificationsOptions {
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
  enabled?: boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { limit = 20, offset = 0, unreadOnly = false, enabled = true } = options;

  return useQuery({
    queryKey: notificationKeys.list({ unreadOnly, limit, offset }),
    queryFn: () => getUserNotifications({ limit, offset, unreadOnly }),
    enabled,
    // Removed polling - now using Supabase Realtime subscription
    // See: NotificationRealtimeProvider for subscription setup
    // F3/F6 fallback: Still refetch on window focus to catch any missed events
    refetchOnWindowFocus: true,
    staleTime: 60_000, // 1 minute — realtime subscription handles live updates, this is a fallback
  });
}

/**
 * Invalidate all notification queries (for use after marking as read, etc.)
 */
export function useInvalidateNotifications() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    queryClient.invalidateQueries({ queryKey: notificationPreferenceKeys.unreadCount() });
  };
}

export type { Notification };
